import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Empty, Modal, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Split the extracted text into blocks (paragraphs and tables). A block whose
 * rows are pipe-delimited (`cell | cell`) is rendered as a real table; the rest
 * are paragraphs. Each block is flagged as highlighted when its text appears in
 * the cited passage.
 */
const parseBlocks = (rawText, passage) => {
  const normPassage = normalize(passage);
  return rawText
    .split(/\n{2,}/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split('\n');
      const nonEmpty = lines.filter((l) => l.trim());
      const pipeLines = lines.filter((l) => l.includes(' | ')).length;
      const bulletLines = nonEmpty.filter((l) =>
        l.trim().startsWith('•')
      ).length;
      const isTable = lines.length >= 2 && pipeLines >= 2;
      const isList =
        !isTable && bulletLines >= 1 && bulletLines === nonEmpty.length;
      const highlighted =
        !!normPassage &&
        lines.some((l) => {
          const n = normalize(l);
          return n.length > 3 && normPassage.includes(n);
        });
      return { block, lines, isTable, isList, highlighted };
    });
};

function TableView({ lines }) {
  const rows = lines
    .filter(Boolean)
    .map((line) => line.split('|').map((cell) => cell.trim()));

  return (
    <table
      style={{
        borderCollapse: 'collapse',
        width: '100%',
        margin: '10px 0',
        fontSize: 13,
      }}
    >
      <tbody>
        {rows.map((cells, ri) => (
          <tr key={ri}>
            {cells.map((cell, ci) =>
              ri === 0 ? (
                <th
                  key={ci}
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: '6px 10px',
                    textAlign: 'left',
                    background: '#fafafa',
                    fontWeight: 600,
                  }}
                >
                  {cell}
                </th>
              ) : (
                <td
                  key={ci}
                  style={{
                    border: '1px solid #d9d9d9',
                    padding: '6px 10px',
                    textAlign: 'left',
                  }}
                >
                  {cell}
                </td>
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Opens a cited knowledge document in a modal, rendering paragraphs and tables
 * with the cited passage softly highlighted and scrolled into view.
 */
export default function DocumentViewer({ open, documentId, chunkId, onClose }) {
  const { t } = useTranslation('assistant');
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const firstHitRef = useRef(null);
  const { message } = App.useApp();

  const handleDownload = () => {
    if (!documentId) return;
    setDownloading(true);
    API.assistant
      .downloadSourceDocument(documentId, doc?.title)
      .catch(() => message.error(t('viewer.downloadError')))
      .finally(() => setDownloading(false));
  };

  useEffect(() => {
    if (!open || !documentId) return undefined;
    let active = true;
    setLoading(true);
    setFailed(false);
    setDoc(null);

    API.assistant
      .getSourceDocument(documentId, chunkId)
      .then((res) => {
        if (active) setDoc(res?.data || null);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, documentId, chunkId]);

  const blocks = useMemo(
    () => (doc?.rawText ? parseBlocks(doc.rawText, doc.passage) : []),
    [doc]
  );

  const firstHitIndex = blocks.findIndex((b) => b.highlighted);
  const hasHighlight = firstHitIndex !== -1;

  // Scroll the first highlighted block into view once rendered.
  useEffect(() => {
    if (!doc || !hasHighlight || !firstHitRef.current) return undefined;
    const id = setTimeout(() => {
      firstHitRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 90);
    return () => clearTimeout(id);
  }, [doc, hasHighlight]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={
        // Custom header so the download sits next to the title; `paddingRight`
        // keeps it clear of Antd's close button.
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            paddingRight: 32,
          }}
        >
          <span>{doc?.title || t('viewer.title')}</span>

          {doc?.hasFile && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading}
              onClick={handleDownload}
            >
              {t('viewer.download')}
            </Button>
          )}
        </div>
      }
      styles={{ body: { maxHeight: '68vh', overflow: 'auto' } }}
      destroyOnClose
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin tip={t('viewer.loading')} />
        </div>
      )}

      {!loading && failed && <Empty description={t('viewer.error')} />}

      {!loading && !failed && doc && (
        <>
          {doc.passage && !hasHighlight && (
            <Alert
              type="info"
              showIcon
              banner
              message={t('viewer.noHighlight')}
              style={{ marginBottom: 12 }}
            />
          )}

          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            {blocks.map((b, i) => {
              const ref = i === firstHitIndex ? firstHitRef : undefined;
              const hlStyle = b.highlighted
                ? {
                    background: '#fffbe6',
                    color: 'rgba(0,0,0,0.85)',
                    borderLeft: '3px solid #faad14',
                    paddingLeft: 8,
                    borderRadius: 2,
                  }
                : undefined;

              return (
                <div
                  key={i}
                  ref={ref}
                  style={{ marginBottom: 10, ...hlStyle }}
                >
                  {b.isTable ? (
                    <TableView lines={b.lines} />
                  ) : b.isList ? (
                    <ul style={{ margin: 0, paddingLeft: 22 }}>
                      {b.lines
                        .filter((l) => l.trim())
                        .map((l, li) => (
                          <li key={li}>{l.replace(/^•\s*/, '')}</li>
                        ))}
                    </ul>
                  ) : (
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {b.block}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
